import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Maximize, Minimize, Sparkles } from "lucide-react";

const BOOKING_URL = "https://www.massagebook.com/biz/lacresharamos";
const DURATION = 28; // seconds — 4 scenes x 6s + 4s CTA finale

// Scene definitions. Each has a start/end window, gradient, palette, headline, sub, stat.
const SCENES = [
  {
    key: "stress",
    start: 0,
    end: 6,
    label: "Stress Relief",
    bg: "radial-gradient(ellipse at 30% 20%, #1e3a8a 0%, #0f172a 55%, #020617 100%)",
    accent: "#5EEAD4",
    headline: "Melt Stress Away",
    sub: "Your cortisol drops. Your nervous system exhales.",
    statValue: 50,
    statSuffix: "%",
    statLabel: "Average cortisol reduction",
  },
  {
    key: "pain",
    start: 6,
    end: 12,
    label: "Pain & Tension",
    bg: "radial-gradient(ellipse at 70% 30%, #7c2d12 0%, #1f1412 55%, #0a0706 100%)",
    accent: "#FCA5A5",
    headline: "Release Chronic Tension",
    sub: "Deep pressure unwinds knots 16 years in the making.",
    statValue: 80,
    statSuffix: "%",
    statLabel: "Fewer tension headaches",
  },
  {
    key: "sleep",
    start: 12,
    end: 18,
    label: "Sleep & Circulation",
    bg: "radial-gradient(ellipse at 25% 70%, #312e81 0%, #1e1b4b 55%, #0b0822 100%)",
    accent: "#C4B5FD",
    headline: "Deeper Sleep, Better Flow",
    sub: "Blood circulation can double. Rest becomes effortless.",
    statValue: 2,
    statSuffix: "x",
    statLabel: "Circulation boost",
  },
  {
    key: "immunity",
    start: 18,
    end: 24,
    label: "Immunity & Wellness",
    bg: "radial-gradient(ellipse at 75% 80%, #064e3b 0%, #042f2e 55%, #011414 100%)",
    accent: "#D4AF37",
    headline: "Build Lasting Wellness",
    sub: "More lymphocytes. Stronger immunity. A better you.",
    statValue: 31,
    statSuffix: "%",
    statLabel: "Lymphocyte increase",
  },
  {
    key: "cta",
    start: 24,
    end: 28,
    label: "Book Now",
    bg: "radial-gradient(ellipse at 50% 50%, #b8860b 0%, #6b4e02 40%, #0f172a 100%)",
    accent: "#FFE68A",
    headline: "Ready to Feel This Good?",
    sub: "Book with Lacresha Ramos, LMT — 16 years of expertise.",
    statValue: null,
    statSuffix: "",
    statLabel: "",
  },
];

const MONTHS = [
  "January: Start the year with a reset.",
  "February: Self-love starts with self-care.",
  "March: Spring into a lighter you.",
  "April: Renewal. For body and mind.",
  "May: Bloom with wellness.",
  "June: Summer-ready. Stress-free.",
  "July: Rest. Recharge. Repeat.",
  "August: End summer feeling brand new.",
  "September: Fall back into your rhythm.",
  "October: Unwind before the rush.",
  "November: Gratitude begins with wellness.",
  "December: Close the year well-kept.",
];

const ease = (t) => 1 - Math.pow(1 - t, 3);

const getSceneAt = (t) => {
  for (let i = 0; i < SCENES.length; i++) {
    if (t >= SCENES[i].start && t < SCENES[i].end) return { scene: SCENES[i], index: i };
  }
  return { scene: SCENES[SCENES.length - 1], index: SCENES.length - 1 };
};

// Decorative SVG art per scene
const SceneArt = ({ sceneKey, progress, accent }) => {
  const p = ease(Math.min(1, progress));
  if (sceneKey === "stress") {
    return (
      <svg viewBox="0 0 400 300" className="w-full h-full">
        {[0, 1, 2, 3].map((i) => (
          <circle
            key={i}
            cx="200"
            cy="150"
            r={20 + i * 30 * p}
            fill="none"
            stroke={accent}
            strokeOpacity={0.35 - i * 0.07}
            strokeWidth={1.5}
          />
        ))}
        <g transform={`translate(200 150)`} opacity={p}>
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return (
              <ellipse
                key={i}
                cx={0}
                cy={-30}
                rx={10}
                ry={28}
                fill={accent}
                fillOpacity={0.18 + i * 0.03}
                transform={`rotate(${(a * 180) / Math.PI})`}
              />
            );
          })}
          <circle cx={0} cy={0} r={10} fill={accent} />
        </g>
      </svg>
    );
  }
  if (sceneKey === "pain") {
    return (
      <svg viewBox="0 0 400 300" className="w-full h-full">
        <g transform="translate(200 150)" opacity={p}>
          <ellipse cx={0} cy={-70} rx={38} ry={42} fill="none" stroke={accent} strokeOpacity={0.6} strokeWidth={2} />
          <path
            d={`M -55 -20 Q 0 -30 55 -20 L 48 80 Q 0 100 -48 80 Z`}
            fill="none"
            stroke={accent}
            strokeOpacity={0.8}
            strokeWidth={2}
          />
          {Array.from({ length: 5 }).map((_, i) => (
            <circle
              key={i}
              cx={-40 + i * 20}
              cy={20 + (i % 2) * 10}
              r={4 + p * 3}
              fill={accent}
              fillOpacity={0.5 + (i % 2) * 0.3}
            >
              <animate attributeName="r" values="3;7;3" dur="1.6s" repeatCount="indefinite" />
            </circle>
          ))}
        </g>
      </svg>
    );
  }
  if (sceneKey === "sleep") {
    return (
      <svg viewBox="0 0 400 300" className="w-full h-full">
        <g opacity={p}>
          <circle cx={300} cy={80} r={40} fill={accent} fillOpacity={0.9} />
          <circle cx={285} cy={70} r={38} fill="#0b0822" />
          {[0, 1, 2].map((i) => (
            <path
              key={i}
              d={`M 0 ${180 + i * 30} Q 100 ${160 + i * 30} 200 ${180 + i * 30} T 400 ${180 + i * 30}`}
              fill="none"
              stroke={accent}
              strokeOpacity={0.35 - i * 0.08}
              strokeWidth={2}
            >
              <animate
                attributeName="d"
                values={`M 0 ${180 + i * 30} Q 100 ${160 + i * 30} 200 ${180 + i * 30} T 400 ${180 + i * 30};M 0 ${180 + i * 30} Q 100 ${200 + i * 30} 200 ${180 + i * 30} T 400 ${180 + i * 30};M 0 ${180 + i * 30} Q 100 ${160 + i * 30} 200 ${180 + i * 30} T 400 ${180 + i * 30}`}
                dur={`${4 + i}s`}
                repeatCount="indefinite"
              />
            </path>
          ))}
        </g>
      </svg>
    );
  }
  if (sceneKey === "immunity") {
    return (
      <svg viewBox="0 0 400 300" className="w-full h-full">
        <g transform="translate(200 150)" opacity={p}>
          <path
            d="M 0 -80 L 60 -50 L 60 30 Q 60 70 0 90 Q -60 70 -60 30 L -60 -50 Z"
            fill="none"
            stroke={accent}
            strokeWidth={2.5}
            strokeOpacity={0.9}
          />
          <path
            d="M -25 10 L -5 30 L 30 -15"
            fill="none"
            stroke={accent}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {Array.from({ length: 6 }).map((_, i) => {
            const a = (i / 6) * Math.PI * 2;
            return (
              <circle
                key={i}
                cx={Math.cos(a) * 110}
                cy={Math.sin(a) * 110}
                r={4}
                fill={accent}
                fillOpacity={0.6}
              >
                <animate attributeName="r" values="3;6;3" dur={`${2 + i * 0.2}s`} repeatCount="indefinite" />
              </circle>
            );
          })}
        </g>
      </svg>
    );
  }
  return null;
};

// Animated counting stat
const AnimatedStat = ({ value, suffix, label, accent, progress }) => {
  if (value === null || value === undefined) return null;
  const p = ease(Math.min(1, progress));
  const shown = typeof value === "number" ? (Number.isInteger(value) ? Math.round(value * p) : (value * p).toFixed(1)) : value;
  return (
    <div className="flex flex-col items-start">
      <div className="flex items-baseline gap-1">
        <span className="text-5xl md:text-7xl font-bold tabular-nums tracking-tight" style={{ color: accent, textShadow: `0 0 24px ${accent}55` }}>
          {shown}
        </span>
        <span className="text-3xl md:text-4xl font-bold" style={{ color: accent }}>{suffix}</span>
      </div>
      <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-white/70 mt-1">{label}</span>
    </div>
  );
};

// Floating particles (persistent decorative layer)
const Particles = () => {
  const dots = useMemo(
    () =>
      Array.from({ length: 28 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 2 + Math.random() * 4,
        dur: 8 + Math.random() * 10,
        delay: Math.random() * -15,
        opacity: 0.15 + Math.random() * 0.35,
      })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((d) => (
        <span
          key={d.id}
          className="mbp-particle"
          style={{
            left: `${d.left}%`,
            width: d.size,
            height: d.size,
            animationDuration: `${d.dur}s`,
            animationDelay: `${d.delay}s`,
            opacity: d.opacity,
          }}
        />
      ))}
    </div>
  );
};

const fmt = (t) => {
  const s = Math.max(0, Math.floor(t));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const MassageBenefitsPlayer = () => {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [fs, setFs] = useState(false);
  const [hovering, setHovering] = useState(false);
  const rafRef = useRef(0);
  const lastRef = useRef(0);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const draggingRef = useRef(false);

  const monthTagline = MONTHS[new Date().getMonth()];

  // Animation driver
  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(rafRef.current);
      lastRef.current = 0;
      return;
    }
    const tick = (ts) => {
      if (!lastRef.current) lastRef.current = ts;
      const dt = (ts - lastRef.current) / 1000;
      lastRef.current = ts;
      setT((prev) => {
        const next = prev + dt;
        return next >= DURATION ? 0 : next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  // Fullscreen listener
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFs = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) await containerRef.current.requestFullscreen();
      else await document.exitFullscreen();
    } catch (_) { /* ignore */ }
  }, []);

  const restart = () => {
    setT(0);
    setPlaying(true);
    lastRef.current = 0;
  };

  const scrubTo = useCallback((clientX) => {
    const bar = progressRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setT(pct * DURATION);
    lastRef.current = 0;
  }, []);

  const onScrubStart = (e) => {
    draggingRef.current = true;
    scrubTo(e.clientX);
    const move = (ev) => draggingRef.current && scrubTo(ev.clientX);
    const up = () => {
      draggingRef.current = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const { scene, index: activeIndex } = getSceneAt(t);
  const sceneProgress = (t - scene.start) / (scene.end - scene.start);
  const progressPct = (t / DURATION) * 100;
  const isCta = scene.key === "cta";
  const ctaPulse = isCta ? ease(Math.min(1, sceneProgress * 1.4)) : 0;

  return (
    <section className="py-16 bg-gradient-to-b from-[#0F172A] to-[#111827]" data-testid="benefits-video-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 px-4 py-2 rounded-full mb-4">
            <Sparkles size={16} className="text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.2em] uppercase">
              Monthly Feature
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-3 font-display">
            Why Massage Works
          </h2>
          <p className="text-slate-400 text-base md:text-lg italic">{monthTagline}</p>
        </div>

        <div
          ref={containerRef}
          className="mbp-container relative w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black"
          style={{ aspectRatio: "16 / 9" }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          data-testid="benefits-video-player"
        >
          {/* Gradient backdrop per scene — cross-fades by rendering all with opacity */}
          {SCENES.map((s) => {
            const active = s.key === scene.key;
            return (
              <div
                key={s.key}
                className="absolute inset-0 transition-opacity duration-700"
                style={{ background: s.bg, opacity: active ? 1 : 0 }}
              />
            );
          })}

          {/* Grain / noise overlay */}
          <div className="mbp-grain absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none" />

          {/* Floating particles */}
          <Particles />

          {/* Scene content */}
          <div className="absolute inset-0 flex flex-col md:flex-row items-center">
            {/* Left: copy */}
            <div className="flex-1 h-full flex flex-col justify-center px-6 md:px-12 py-8 relative z-10">
              {SCENES.filter((s) => s.key !== "cta").map((s) => {
                const active = s.key === scene.key;
                const p = active ? ease(Math.min(1, sceneProgress * 1.3)) : 0;
                return (
                  <div
                    key={s.key}
                    className="absolute inset-0 px-6 md:px-12 flex flex-col justify-center transition-opacity duration-500"
                    style={{ opacity: active ? 1 : 0, pointerEvents: "none" }}
                  >
                    <span
                      className="text-xs font-bold tracking-[0.3em] uppercase mb-4"
                      style={{ color: s.accent, opacity: p, transform: `translateY(${(1 - p) * 12}px)`, transition: "transform 400ms" }}
                    >
                      Chapter {SCENES.indexOf(s) + 1} — {s.label}
                    </span>
                    <h3
                      className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 font-display"
                      style={{ opacity: p, transform: `translateY(${(1 - p) * 18}px)`, transition: "transform 500ms" }}
                    >
                      {s.headline}
                    </h3>
                    <p
                      className="text-slate-200/90 text-base md:text-lg mb-6 max-w-md"
                      style={{ opacity: p * 0.95, transform: `translateY(${(1 - p) * 14}px)`, transition: "transform 600ms" }}
                    >
                      {s.sub}
                    </p>
                    <div style={{ opacity: p }}>
                      <AnimatedStat
                        value={s.statValue}
                        suffix={s.statSuffix}
                        label={s.statLabel}
                        accent={s.accent}
                        progress={sceneProgress * 1.2}
                      />
                    </div>
                  </div>
                );
              })}

              {/* CTA finale */}
              {isCta && (
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                  <span
                    className="text-xs font-bold tracking-[0.3em] uppercase mb-4"
                    style={{ color: "#FFE68A", opacity: ctaPulse }}
                  >
                    The Only Thing Missing Is You
                  </span>
                  <h3
                    className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 font-display"
                    style={{ opacity: ctaPulse, transform: `scale(${0.9 + ctaPulse * 0.1})`, transition: "transform 500ms" }}
                  >
                    Ready to Feel This Good?
                  </h3>
                  <p className="text-slate-200 text-base md:text-xl mb-8 max-w-xl" style={{ opacity: ctaPulse }}>
                    Book with Lacresha Ramos, LMT — 16 years of expertise in Daytona Beach.
                  </p>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mbp-cta-glow inline-flex items-center gap-3 px-10 md:px-14 py-4 md:py-5 rounded-full text-[#0F172A] text-lg md:text-xl font-bold tracking-wide"
                    style={{
                      background: "linear-gradient(135deg, #FFE68A 0%, #D4AF37 50%, #A37B00 100%)",
                      boxShadow: `0 0 ${40 + ctaPulse * 40}px ${10 + ctaPulse * 20}px rgba(212,175,55,${0.35 + ctaPulse * 0.35})`,
                      transform: `scale(${0.95 + ctaPulse * 0.08})`,
                    }}
                    data-testid="player-cta-book-now"
                  >
                    <Sparkles size={22} />
                    BOOK NOW
                  </a>
                  <p className="text-slate-300/80 text-sm mt-6" style={{ opacity: ctaPulse * 0.9 }}>
                    Limited appointments available this week.
                  </p>
                </div>
              )}
            </div>

            {/* Right: SVG art panel (hidden on small) */}
            {!isCta && (
              <div className="hidden md:flex w-1/2 h-full items-center justify-center relative z-10 pointer-events-none">
                {SCENES.filter((s) => s.key !== "cta").map((s) => {
                  const active = s.key === scene.key;
                  return (
                    <div
                      key={s.key}
                      className="absolute inset-0 flex items-center justify-center p-8 transition-opacity duration-500"
                      style={{ opacity: active ? 1 : 0 }}
                    >
                      <SceneArt sceneKey={s.key} progress={active ? sceneProgress * 1.2 : 0} accent={s.accent} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top bar — scene label */}
          <div
            className="absolute top-0 left-0 right-0 p-4 md:p-6 flex items-center justify-between z-20 pointer-events-none"
            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)" }}
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-white/80 text-xs md:text-sm font-semibold tracking-widest uppercase">
                Millennium Wellness • {scene.label}
              </span>
            </div>
            <div className="text-white/60 text-xs md:text-sm tabular-nums">{fmt(t)} / {fmt(DURATION)}</div>
          </div>

          {/* Bottom: controls */}
          <div
            className={`absolute left-0 right-0 bottom-0 z-20 px-4 md:px-6 pb-4 md:pb-5 pt-10 transition-opacity duration-300`}
            style={{
              opacity: hovering || !playing ? 1 : 0.9,
              background: "linear-gradient(to top, rgba(0,0,0,0.75), transparent)",
            }}
          >
            {/* Chapter dots above progress */}
            <div className="flex items-center gap-2 mb-2">
              {SCENES.map((s, i) => {
                const active = i === activeIndex;
                return (
                  <button
                    key={s.key}
                    onClick={() => { setT(s.start + 0.01); lastRef.current = 0; }}
                    className="group flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
                    aria-label={`Jump to ${s.label}`}
                    data-testid={`player-chapter-${s.key}`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full transition-all"
                      style={{
                        background: active ? s.accent : "rgba(255,255,255,0.4)",
                        boxShadow: active ? `0 0 10px ${s.accent}` : "none",
                        transform: active ? "scale(1.4)" : "scale(1)",
                      }}
                    />
                    <span className={`text-[10px] md:text-xs tracking-widest uppercase transition-colors ${active ? "text-white" : "text-white/50 group-hover:text-white/80"}`}>
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Scrubbable progress bar */}
            <div
              ref={progressRef}
              onPointerDown={onScrubStart}
              className="relative w-full h-1.5 bg-white/15 rounded-full cursor-pointer group/bar"
              data-testid="player-progress-bar"
            >
              {/* Chapter tick segments */}
              {SCENES.map((s) => (
                <span
                  key={s.key}
                  className="absolute top-0 bottom-0 w-px bg-white/25"
                  style={{ left: `${(s.start / DURATION) * 100}%` }}
                />
              ))}
              <div
                className="absolute top-0 left-0 bottom-0 rounded-full transition-[width] duration-75"
                style={{
                  width: `${progressPct}%`,
                  background: `linear-gradient(90deg, ${SCENES[0].accent} 0%, ${scene.accent} 100%)`,
                  boxShadow: `0 0 10px ${scene.accent}88`,
                }}
              />
              <div
                className="absolute top-1/2 w-3 h-3 rounded-full bg-white shadow-lg opacity-0 group-hover/bar:opacity-100 transition-opacity"
                style={{ left: `calc(${progressPct}% - 6px)`, transform: "translateY(-50%)" }}
              />
            </div>

            {/* Buttons row */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label={playing ? "Pause" : "Play"}
                  data-testid="player-playpause-btn"
                >
                  {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
                </button>
                <button
                  onClick={restart}
                  className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label="Restart"
                  data-testid="player-restart-btn"
                >
                  <RotateCcw size={18} />
                </button>
                <button
                  onClick={() => setMuted((m) => !m)}
                  className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label={muted ? "Unmute" : "Mute"}
                  data-testid="player-mute-btn"
                >
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37] hover:bg-[#E4BF47] text-[#0F172A] text-xs font-bold tracking-wider uppercase transition-all hover:scale-105"
                  data-testid="player-booknow-inline"
                >
                  Book Now
                </a>
                <button
                  onClick={toggleFs}
                  className="p-2 rounded-full hover:bg-white/10 text-white transition-colors"
                  aria-label={fs ? "Exit fullscreen" : "Fullscreen"}
                  data-testid="player-fullscreen-btn"
                >
                  {fs ? <Minimize size={18} /> : <Maximize size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* External CTA — reinforces the end */}
        <div className="text-center mt-8">
          <p className="text-slate-400 text-sm mb-4">
            Experience these benefits for yourself — book your session today.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <button
              className="mbp-cta-glow inline-flex items-center gap-3 px-10 py-4 rounded-none text-[#0F172A] text-base md:text-lg font-bold tracking-wider uppercase"
              style={{
                background: "linear-gradient(135deg, #FFE68A 0%, #D4AF37 50%, #A37B00 100%)",
              }}
              data-testid="benefits-video-book-now"
            >
              BOOK YOUR MASSAGE NOW
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default MassageBenefitsPlayer;
